import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';

interface Destination {
  id: string;
  name: string;
  image: string;
  link: string;
}

const PopularDestinations: React.FC = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  const destinations: Destination[] = [
    {
      id: 'campo_imperatore',
      name: t('landing.destinations.campoImperatore.title'),
      image: '/destinations/campo_imperatore/campo_imperatore_homepage.webp',
      link: '/destination/campo_imperatore'
    },
    {
      id: 'collalto_sabino',
      name: t('landing.destinations.collaltoSabino.title'),
      image: '/destinations/collalto_sabino/collalto_sabino_homepage.webp',
      link: '/destination/collalto_sabino'
    },
    {
      id: 'rocca_calascio',
      name: t('landing.destinations.roccaCalascio.title'),
      image: '/destinations/rocca_calascio/rocca_calascio_homepage.webp',
      link: '/destination/rocca_calascio'
    },
    {
      id: 'santo_stefano_di_sessanio',
      name: t('landing.destinations.santoStefanoDiSessanio.title'),
      image: '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_homepage.webp',
      link: '/destination/santo_stefano_di_sessanio'
    },
    {
      id: 'scanno',
      name: t('landing.destinations.scanno.title'),
      image: '/destinations/scanno/scanno_homepage.webp',
      link: '/destination/scanno'
    },
    {
      id: 'trabocchi_coast',
      name: t('landing.destinations.trabocchiCoast.title'),
      image: '/destinations/trabocchi_coast/trabocchi_coast_homepage.webp',
      link: '/destination/trabocchi_coast'
    }
  ];

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(destinations.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(destinations.length / 3)) % Math.ceil(destinations.length / 3));
  };

  const renderDestinationCard = (destination: Destination) => {
    // Skip placeholder destinations (those without names)
    if (!destination.name) {
      return <div key={destination.id} className="col flex-1 min-w-0"></div>;
    }
    
    return (
      <div key={destination.id} className="col flex-1 min-w-0">
        <div className="img-holder relative rounded-lg overflow-hidden shadow-lg">
          <Link to={destination.link}>
            <img 
              src={destination.image} 
              alt={destination.name}
              className="w-full h-64 object-cover transition-transform duration-300 hover:scale-105"
            />
          </Link>
        </div>
        <h2 className="trip-title text-center mt-4">
          <Link 
            to={destination.link} 
            className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
          >
            {destination.name}
          </Link>
        </h2>
      </div>
    );
  };

  return (
    <section id="destinations" className="popular-destination py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <header className="section-header text-center mb-12">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-4">
            {t('landing.destinations.title')}
          </h2>
          <div className="section-content">
            <p className="text-gray-600 text-lg">
              {t('landing.destinations.subtitle')}
            </p>
          </div>
        </header>

        {/* Mobile: List View */}
        {isMobile && (
          <div className="grid grid-cols-1 gap-8 max-w-4xl mx-auto">
            {destinations.map(renderDestinationCard)}
          </div>
        )}

        {/* Desktop/Tablet: Slider View */}
        {!isMobile && (
          <div className="max-w-7xl mx-auto">
            {/* Slider Container with Side Navigation */}
            <div className="flex items-center gap-4">
              {/* Left Navigation Button */}
              <button
                onClick={prevSlide}
                className="flex-shrink-0 bg-white hover:bg-gray-50 text-gray-800 p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110 border border-gray-200"
                aria-label="Previous destinations"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Slider Content */}
              <div className="flex-1 overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ 
                    transform: `translateX(-${currentSlide * 100}%)`
                  }}
                >
                  {Array.from({ length: Math.ceil(destinations.length / 3) }, (_, slideIndex) => (
                    <div key={slideIndex} className="flex gap-8 w-full flex-shrink-0">
                      {destinations.slice(slideIndex * 3, slideIndex * 3 + 3).map(renderDestinationCard)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Navigation Button */}
              <button
                onClick={nextSlide}
                className="flex-shrink-0 bg-white hover:bg-gray-50 text-gray-800 p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110 border border-gray-200"
                aria-label="Next destinations"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: Math.ceil(destinations.length / 3) }, (_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-blue-600 scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default PopularDestinations; 